import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  Breadcrumbs,
  Typography,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { saveAs } from 'file-saver';
import { makeStyles } from '@material-ui/core/styles';
import { Link, useHistory, useLocation } from 'react-router-dom';
import TextTypo from 'components/helpers/TextTypo';
import {
  specificLoanApplicationAction,
  downloadAction,
  zipDownloadAction,
  changeApplicationStatusAction,
  downloadDocumentAction,
} from 'redux/actions/applicationAction';
import NumberFormat from 'react-number-format';
import { Form, Tag, Modal, Card } from 'antd';
import { decodeToken, downloadFile, getStatusColor } from 'utils';
import { CheckCircle, RemoveRedEye } from '@material-ui/icons';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  EyeFilled,
} from '@ant-design/icons';
import Button from 'components/Button';
import CommentForm from 'components/Comment';
import Input from 'components/Input';
import Select from 'components/Select';
import { inputRules } from 'utils';
import { Buffer } from 'buffer';

const useStyles = makeStyles({
  tableCell: {
    fontWeight: 'bold',
  },
  back: {
    cursor: 'pointer',
    color: '#198754',
  },
});

const SpecificApplication = ({ match }) => {
  const { applicationId } = match.params;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const [approvalForm] = Form.useForm();

  const [isDownloadCompleted, setIsDownloadCompleted] = useState(false);
  const [isZipDownloadCompleted, setIsZipDownloadCompleted] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const [statusComment, setStatusComment] = useState();
  const [showModal, setShowModal] = useState(false);
  const [appAction, setAppAction] = useState('');
  const [bankStatus, setBankStatus] = useState('');

  const { application, applicationStatus, downloadedDoc } = useSelector(
    ({ backendUsers, specificLoanApplication }) => ({
      application: specificLoanApplication,
      applicationStatus: backendUsers?.applicationStatus,
      downloadedDoc: backendUsers?.downloadedDoc,
    })
  );
  const results = useSelector((state) => state.certificate);
  const zip = useSelector((state) => state.zipCertificate);

  // useEffect(() => {
  //   if (downloadedDoc?.data) {
  //     downloadFile(downloadedDoc?.data);
  //   }
  // }, [downloadedDoc]);

  if (results.data) {
    const buffer = Buffer.from(results.data.fileBlob.data);
    const blob = new Blob([buffer], {
      type: `${results?.data?.fileType};charset=utf-8`,
    });
    if (isDownloadCompleted) {
      saveAs(blob, results.data.fileName);
    }
  }

  if (zip.data) {
    const buffer = Buffer.from(zip.data.fileBlob.data);
    const blob = new Blob([buffer], {
      type: `${zip?.data?.fileType};charset=utf-8`,
    });
    if (isZipDownloadCompleted) {
      saveAs(blob, zip.data.fileName);
    }
  }

  const handleWorkCertificate = () => {
    dispatch(downloadAction(applicationId, 'applicant', 'workCertificate'));
    setIsDownloadCompleted(true);
    setIsZipDownloadCompleted(false);
  };

  const handleSpouseWorkCertificate = () => {
    dispatch(
      downloadAction(applicationId, 'applicant', 'spouse.workCertificate')
    );
    setIsDownloadCompleted(true);
    setIsZipDownloadCompleted(false);
  };

  const handleMaritalCertificate = () => {
    dispatch(
      downloadAction(applicationId, 'applicant', 'maritalStatusCertificate')
    );
    setIsDownloadCompleted(true);
    setIsZipDownloadCompleted(false);
  };

  const handleZipDownload = () => {
    dispatch(zipDownloadAction(applicationId));
    setIsZipDownloadCompleted(true);
    setIsDownloadCompleted(false);
  };

  const handleBack = () => {
    history.goBack();
  };

  useEffect(() => {
    dispatch(specificLoanApplicationAction(applicationId));
  }, []);

  useEffect(() => {
    if (applicationStatus?.status === 200) {
      dispatch(specificLoanApplicationAction(applicationId));
      setShowModal(false);
    }
  }, [applicationStatus]);

  const getUPIlocation = (objString) => {
    const obj = JSON.parse(objString);
    const {
      cell: { cellName },
      sector: { sectorName },
      district: { districtName },
      province: { provinceName },
    } = obj || {};
    return `${provinceName} , ${districtName}, ${sectorName}, ${cellName}`;
  };

  const level =
    decodeToken()?.userRole?.RoleName === 'Bank Officer' ? 'bank' : 'backend';

  const handleApproval = () => {
    approvalForm
      .validateFields()
      .then(({ status, otherStatusComment, ...payload }) => {
        const applicationStatus = level === 'bank' ? status : appAction.action;
        if (statusComment === 'Other') {
          payload.statusComment = otherStatusComment;
        }
        changeApplicationStatusAction(
          applicationId,
          applicationStatus,
          level,
          payload
        )(dispatch);
      })
      .catch(() => {
        return;
      });
  };

  const handleDownload = (docId) => {
    dispatch(downloadDocumentAction(applicationId, docId));
  };

  const availAction = (level) => {
    const role = decodeToken().userRole?.RoleName;

    if (role === 'Back-End Officer' && level === 'backend-officer') {
      return true;
    }
    if (role === 'Bank Officer' && level === 'bank') {
      return true;
    }
    return false;
  };

  return (
    <div style={{ marginTop: 20 }}>
      <Modal
        title={`${appAction.title || 'Update'} Application`}
        open={showModal}
        onOk={handleApproval}
        okText="Save Changes"
        onCancel={() => setShowModal(false)}
        confirmLoading={applicationStatus?.loading}
      >
        <Form form={approvalForm}>
          {level !== 'bank' ? (
            <>
              {level === 'bank' && appAction.action === 'Approved' && (
                <div>
                  <Form.Item name="approvedLoanAmount" rules={inputRules}>
                    <Input label="Loan Amount Disbursed" required />
                  </Form.Item>
                  <Form.Item name="interestRate">
                    <Input label="Interest rate" />
                  </Form.Item>
                </div>
              )}
              {appAction.action === 'Rejected' && (
                <div>
                  <Form.Item name="statusComment" rules={inputRules}>
                    <Select
                      placeholder="Select reason"
                      options={[
                        { value: 'Plot Issue' },
                        { value: 'Income Issue' },
                        { value: 'Certificate Issue' },
                        { value: 'Other' },
                      ]}
                      size="large"
                      onChange={setStatusComment}
                    />
                  </Form.Item>
                  {statusComment === 'Other' && (
                    <Form.Item
                      name="otherStatusComment"
                      rules={[
                        {
                          required: true,
                          message: 'Comment is required',
                        },
                      ]}
                    >
                      <Input placeholder="Please specify the reason" />
                    </Form.Item>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <Form.Item name="status" rules={inputRules}>
                <Select
                  options={[
                    {
                      value: 'Under-Process',
                    },
                    {
                      value: 'Disbursed',
                    },
                  ]}
                  placeholder="Select status"
                  label="Status"
                  onChange={setBankStatus}
                  required
                />
              </Form.Item>
              {bankStatus === 'Disbursed' && (
                <>
                  <Form.Item name="housePurchaseAmount" rules={inputRules}>
                    <Input
                      type="number"
                      onWheel="return false;"
                      label="Property Purchase Amount"
                      required
                    />
                  </Form.Item>
                  <Form.Item
                    name="approvedLoanAmount"
                    rules={[
                      {
                        required: true,
                        message: 'Loan amount is required',
                      },
                    ]}
                  >
                    <Input label="Loan Amount Disbursed" required />
                  </Form.Item>

                  <Form.Item name="disbursedDate">
                    <Input
                      type="date"
                      label="Loan Start Date"
                      defaultFocus={true}
                    />
                  </Form.Item>
                  <Form.Item name="maturityDate">
                    <Input
                      type="date"
                      label="Loan Maturity Date"
                      defaultFocus={true}
                    />
                  </Form.Item>
                  <Form.Item name="interestRate">
                    <Input label="Interest rate" />
                  </Form.Item>

                  <Form.Item name="perfectionStatus">
                    <Input label="Perfection Status" />
                  </Form.Item>

                  <Form.Item name="fireInsuranceExpiryDate">
                    <Input
                      type="date"
                      label="Fire Insurance Expiry Date"
                      defaultFocus={true}
                    />
                  </Form.Item>

                  <Form.Item name="houseUpi" rules={inputRules}>
                    <Input label="Property UPI" required />
                  </Form.Item>

                  <Card style={{ width: 300 }}>
                    <p>Loan To Value Ratio : 1</p>
                    <p>Tenor in months : 1</p>
                  </Card>
                </>
              )}
            </>
          )}
        </Form>
      </Modal>
      <Container>
        <Grid container direction="column">
          <Grid item>
            <Breadcrumbs aria-label="breadcrumb">
              <Typography onClick={handleBack} className={classes.back}>
                {t('back')}
              </Typography>
              <Typography>...</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item>
            <h5 className="my-5">
              Loan Application - <b>{application?.data?.referenceId}</b>
            </h5>
          </Grid>

          <div className="d-flex">
            <div className="w-100">
              <Grid item className="d-flex flex-column mt-3">
                <div className="card p-3">
                  <div className="d-flex justify-content-between">
                    <h5 style={{ fontWeight: '700' }}>Application Status</h5>
                    <label className="text-muted mx-3">
                      {' '}
                      <small>
                        <RemoveRedEye /> {application?.data?.views?.total}
                      </small>
                    </label>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between py-3">
                    <div>
                      <div className="d-flex">
                        <label className={classes.tableCell}>
                          RHA Status: &nbsp;
                        </label>
                        <label>
                          {' '}
                          <Tag
                            color={getStatusColor(
                              application?.data?.backEndStatus
                            )}
                          >
                            {application?.data?.backEndStatus}
                          </Tag>
                        </label>
                      </div>
                      <div className="d-flex">
                        <label className={classes.tableCell}>
                          Officer: &nbsp;
                        </label>
                        <label>
                          {application?.data?.backEndStatusChangedByUser
                            ? application?.data?.backEndStatusChangedByUser
                                ?.firstName +
                              ' ' +
                              application?.data?.backEndStatusChangedByUser
                                ?.lastName
                            : 'N/A'}
                        </label>
                      </div>
                    </div>
                    <div className=" mx-5">
                      <div className="d-flex">
                        <label className={classes.tableCell}>
                          Bank Status: &nbsp;
                        </label>
                        <label>
                          <Tag
                            color={getStatusColor(
                              application?.data?.bankStatus
                            )}
                          >
                            {application?.data?.bankStatus}
                          </Tag>
                        </label>
                      </div>
                      <div className="d-flex">
                        <label className={classes.tableCell}>
                          Officer: &nbsp;
                        </label>
                        <label>
                          {application?.data?.bankStatusChangedByUser
                            ? application?.data?.bankStatusChangedByUser
                                ?.firstName +
                              ' ' +
                              application?.data?.bankStatusChangedByUser
                                ?.lastName
                            : 'N/A'}
                        </label>
                      </div>
                    </div>
                    <div className=" mx-5">
                      <div className="d-flex">
                        <label className={classes.tableCell}>
                          Level: &nbsp;
                        </label>
                        <label className="text-capitalize">
                          <Tag>{application?.data?.level}</Tag>
                        </label>
                      </div>
                    </div>
                  </div>
                  {level === 'bank' ? (
                    <div className="d-flex justify-content-center my-2">
                      <Button type="primary" onClick={() => setShowModal(true)}>
                        Update Status
                      </Button>
                    </div>
                  ) : (
                    availAction(application?.data?.level?.toLowerCase()) && (
                      <div className="d-flex justify-content-center py-3">
                        <Button
                          onClick={() => {
                            setAppAction({
                              action: 'Approved',
                              title: 'Approve',
                            });
                            setShowModal(true);
                          }}
                          className="text-success mx-2"
                        >
                          Approve
                          <CheckCircleFilled />
                        </Button>
                        <Button
                          onClick={() => {
                            setAppAction({
                              action: 'Rejected',
                              title: 'Reject',
                            });
                            setShowModal(true);
                          }}
                          className="text-danger"
                        >
                          Reject
                          <CloseCircleFilled />
                        </Button>
                      </div>
                    )
                  )}
                </div>

                <div className="card p-3 my-2">
                  <h5>Loan Info</h5>
                  <hr />
                  <div className="d-flex justify-content-between py-3">
                    <div>
                      <div className="d-flex flex-column">
                        <label className={classes.tableCell}>
                          Household Income
                        </label>
                        <label>
                          RWF {application?.data?.rraCombinedHouseHoldIncome}
                        </label>
                      </div>
                      <div className="d-flex flex-column mt-3">
                        <label className={classes.tableCell}>
                          Deductable Income
                        </label>
                        <label>
                          RWF{' '}
                          {application?.data?.deductableIncome?.toLocaleString()}
                        </label>
                      </div>
                    </div>

                    <div className="mx-5">
                      <div className="d-flex flex-column">
                        <label className={classes.tableCell}>
                          Property Limit
                        </label>
                        <label>
                          RWF{' '}
                          {application?.data?.propertyValueLimit?.toLocaleString()}
                        </label>
                      </div>
                      <div className="d-flex flex-column mt-3">
                        <label className={classes.tableCell}>
                          Present Value
                        </label>
                        <label>
                          RWF{' '}
                          {application?.data?.presentValue?.toLocaleString()}
                        </label>
                      </div>
                    </div>

                    <div className="mx-5">
                      <div className="d-flex flex-column">
                        <label className={classes.tableCell}>
                          Loan Provider (1st)
                        </label>
                        <label> {application?.data?.bankProvider1?.name}</label>
                      </div>
                      <div className="d-flex flex-column mt-3">
                        <label className={classes.tableCell}>
                          Loan Provider (2nd)
                        </label>
                        <label>
                          {application?.data?.bankProvider2?.name || 'None'}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </Grid>
              <div className="card p-3 my-2">
                <h5>Personal Info</h5>
                <hr />
                <div className="d-flex justify-content-between flex-wrap">
                  <div>
                    <div className="d-flex flex-column">
                      <label className={classes.tableCell}>Names</label>
                      <label>
                        {application?.data?.applicant?.firstName +
                          ' ' +
                          application?.data?.applicant?.lastName}
                      </label>
                    </div>
                    <div className="d-flex flex-column mt-3">
                      <label className={classes.tableCell}>
                        {t('id-number')}
                      </label>
                      <label>{application?.data?.applicant?.nationalId}</label>
                    </div>
                  </div>
                  <div>
                    <div className="d-flex flex-column">
                      <label className={classes.tableCell}>{t('dob')}</label>
                      <label>{application?.data?.applicant?.dob}</label>
                    </div>
                    <div className="d-flex flex-column mt-3">
                      <label className={classes.tableCell}>{t('gender')}</label>
                      <label>{application?.data?.applicant?.gender}</label>
                    </div>
                  </div>
                  <div>
                    <div className="d-flex flex-column">
                      <label className={classes.tableCell}>
                        {t('marital-status')}
                      </label>
                      <label>
                        {application?.data?.applicant?.maritalStatus}
                      </label>
                    </div>
                    <div className="d-flex flex-column mt-3">
                      <label className={classes.tableCell}>{t('phone')}</label>
                      <label>{application?.data?.applicant?.phone}</label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card p-3 my-2">
                <h5>Employment Info</h5>
                <hr />
                <div className="d-flex justify-content-between">
                  <div>
                    <div className="d-flex flex-column">
                      <label className={classes.tableCell}>
                        {' '}
                        {t('employer')}
                      </label>
                      <label>
                        {application?.data?.applicant?.employerName || 'N/A'}
                      </label>
                    </div>
                    <div className="d-flex flex-column mt-2">
                      <label style={{ fontWeight: 'bold' }}>Employer TIN</label>
                      <label>
                        {application?.data?.applicant?.rraEmployerTin || 'N/A'}
                      </label>
                    </div>
                  </div>
                  <div>
                    <div className="d-flex flex-column">
                      <label className={classes.tableCell}>
                        {t('profession')}
                      </label>
                      <label>{application?.data?.applicant?.profession}</label>
                    </div>
                  </div>
                  <div>
                    <div className="d-flex flex-column">
                      <label className={classes.tableCell}>{t('Salary')}</label>
                      <label>
                        RWF{' '}
                        {application?.data?.applicant?.salarynet.toLocaleString()}
                      </label>
                    </div>
                    <div className="d-flex flex-column mt-2">
                      <label style={{ fontWeight: 'bold' }}>
                        Gross Income (RRA)
                      </label>
                      <label>
                        RWF{' '}
                        {application?.data?.applicant?.rraGrossIncome.toLocaleString()}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card p-3 my-2">
                <h5>UPI Info</h5>
                <hr />

                {application?.data?.applicant?.upis?.length > 0 ? (
                  application?.data?.applicant?.upis?.map((u, key) => (
                    <div
                      className="d-flex justify-content-between my-2"
                      key={key}
                    >
                      <div>
                        <div className="d-flex flex-column">
                          <label className={classes.tableCell}>
                            {t('upi')}
                          </label>
                          <label>{u.upi}</label>
                        </div>
                        <div className="d-flex flex-column mt-3">
                          <label className={classes.tableCell}>
                            {t('land-usage')}
                          </label>
                          <label>{u.landUseNameEnglish}</label>
                        </div>
                      </div>
                      <div>
                        <div className="d-flex flex-column">
                          <label className={classes.tableCell}>
                            {t('primary-owner')}
                          </label>
                          <label>{u.primaryOwner}</label>
                        </div>
                        <div className="d-flex flex-column mt-3">
                          <label className={classes.tableCell}>
                            {t('under-mortgage')}
                          </label>
                          <label>{u.isUnderMortgage ? 'Yes' : 'No'}</label>
                        </div>
                      </div>
                      <div>
                        <div className="d-flex flex-column">
                          <label className={classes.tableCell}>
                            {t('is-developed')}
                          </label>
                          <label>{u.isDeveloped === null ? 'No' : 'Yes'}</label>
                        </div>
                        <div className="d-flex flex-column mt-3">
                          <label className={classes.tableCell}>
                            {t('parcel-location')}
                          </label>
                          <label>{getUPIlocation(u.parcelLocation)}</label>
                        </div>
                      </div>
                      <hr />
                    </div>
                  ))
                ) : (
                  <label className="text-muted px-2">No UPI Info</label>
                )}
              </div>

              <hr />
              <div className="card p-3 my-2">
                <h5 className="font-weight-bold">Spouse Personal Info</h5>
                <hr />
                {application?.data?.applicant?.spouse ? (
                  <div className="d-flex justify-content-between">
                    <div>
                      <div className="d-flex flex-column">
                        <label style={{ fontWeight: 'bold' }}>Name</label>
                        <label>
                          {application?.data?.applicant?.spouse?.firstName +
                            ' ' +
                            application?.data?.applicant?.spouse?.lastName}
                        </label>
                      </div>
                      <div className="d-flex flex-column mt-2">
                        <label style={{ fontWeight: 'bold' }}>
                          National ID
                        </label>
                        <label>
                          {application?.data?.applicant?.spouse?.nationalId}
                        </label>
                      </div>
                    </div>
                    <div>
                      <div className="d-flex flex-column">
                        <label style={{ fontWeight: 'bold' }}>Employer</label>
                        <label>
                          {application?.data?.applicant?.spouse?.employerName ||
                            'N/A'}
                        </label>
                      </div>
                      <div className="d-flex flex-column mt-2">
                        <label style={{ fontWeight: 'bold' }}>Profession</label>
                        <label>
                          {application?.data?.applicant?.spouse?.profession ||
                            'N/A'}
                        </label>
                      </div>
                    </div>
                    <div>
                      <div className="d-flex flex-column">
                        <label style={{ fontWeight: 'bold' }}>Net Salary</label>
                        <label>
                          RWF{' '}
                          {application?.data?.applicant?.spouse?.salarynet?.toLocaleString()}
                        </label>
                      </div>
                      <div className="d-flex flex-column mt-2">
                        <label style={{ fontWeight: 'bold' }}>
                          Gross Income (RRA)
                        </label>
                        <label>
                          {'RWF ' +
                            application?.data?.applicant?.spouse?.rraGrossIncome?.toLocaleString() ||
                            'N/A'}
                        </label>
                      </div>
                    </div>
                  </div>
                ) : (
                  <label className="text-muted px-2">No Spouse Info</label>
                )}
              </div>

              <div className="card p-3 my-2">
                <h5 className="font-weight-bold">Attachments</h5>
                <hr />
                <div className="d-flex">
                  <div>
                    <div className="d-flex flex-column">
                      <label
                        className={`btn btn-link ${classes.tableCell}`}
                        onClick={handleZipDownload}
                      >
                        {' '}
                        {t('zip-download')}
                      </label>
                    </div>
                    <div className="d-flex flex-column">
                      <label
                        className={`btn btn-link ${classes.tableCell}`}
                        onClick={handleWorkCertificate}
                      >
                        {t('workCertificate')}
                      </label>
                    </div>
                  </div>
                  <div className="mx-5">
                    <div className="d-flex flex-column">
                      <label
                        className={`btn btn-link ${classes.tableCell}`}
                        onClick={handleSpouseWorkCertificate}
                      >
                        {t('spouseWorkCertificate')}
                      </label>
                    </div>
                    <div className="d-flex flex-column">
                      <label
                        className={`btn btn-link ${classes.tableCell}`}
                        onClick={handleMaritalCertificate}
                      >
                        {t('maritalCertificate')}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mx-3">
              <label
                onClick={() => setShowComments(!showComments)}
                className="btn btn-link"
              >
                {showComments ? 'Hide' : 'Show'} Comments
              </label>
              {showComments && (
                <CommentForm
                  applicationComments={application?.data?.documents?.sort(
                    (a, b) => b.id - a.id
                  )}
                  handleDownload={handleDownload}
                />
              )}
            </div>
          </div>
        </Grid>
      </Container>
      <div>&nbsp;</div>
    </div>
  );
};

export default SpecificApplication;
